'use client';

import { useState } from 'react';
import Icon from '@/components/ui/AppIcon';
import PropTypes from 'prop-types';

function SuccessMessage({ onClose }) {
  return (
    <div className="text-center py-8">
      <div className="w-16 h-16 bg-success/10 rounded-full flex items-center justify-center mx-auto mb-4">
        <Icon name="CheckCircleIcon" size={32} className="text-success" />
      </div>
      <h3 className="text-xl font-crimson font-semibold text-foreground mb-2">
        Welcome to Our Community!
      </h3>
      <p className="text-muted-foreground font-source-serif mb-4">
        Thank you for subscribing. You'll receive your first daily verse tomorrow morning.
      </p>
      <button
        onClick={onClose}
        className="text-primary font-inter font-medium hover:underline"
      >
        Continue Exploring
      </button>
    </div>
  );
}

SuccessMessage.propTypes = {
  onClose: PropTypes?.func?.isRequired
};

export default function NewsletterSignup() {
  const [email, setEmail] = useState('');
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!email?.trim()) return;

    setIsLoading(true);
    
    // Simulate API call
    setTimeout(() => {
      setIsLoading(false);
      setIsSubscribed(true);
    }, 1500);
  };

  const handleReset = () => {
    setIsSubscribed(false);
    setEmail('');
  };

  if (isSubscribed) {
    return (
      <section className="py-16 bg-gradient-to-r from-primary/10 via-accent/10 to-secondary/10">
        <div className="max-w-4xl mx-auto px-4">
          <div className="bg-card rounded-2xl p-8 border border-border shadow-lg">
            <SuccessMessage onClose={handleReset} />
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-16 bg-gradient-to-r from-primary/10 via-accent/10 to-secondary/10">
      <div className="max-w-4xl mx-auto px-4">
        <div className="bg-card rounded-2xl p-8 border border-border shadow-lg">
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <Icon name="EnvelopeIcon" size={32} className="text-primary" />
            </div>
            
            <h2 className="text-3xl md:text-4xl font-crimson font-semibold text-foreground mb-4">
              Daily Divine Inspiration
            </h2>
            
            <p className="text-lg text-muted-foreground font-source-serif max-w-2xl mx-auto">
              Start each day with carefully selected verses, feminine wisdom insights, 
              and spiritual reflections delivered to your inbox every morning.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="max-w-md mx-auto">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex-1">
                <label htmlFor="email" className="sr-only">
                  Email address
                </label>
                <input
                  type="email"
                  id="email"
                  value={email}
                  onChange={(e) => setEmail(e?.target?.value)}
                  placeholder="Enter your email address"
                  required
                  className="w-full px-4 py-3 rounded-xl border border-border bg-background text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent font-inter"
                />
              </div>
              
              <button
                type="submit"
                disabled={isLoading || !email?.trim()}
                className="px-8 py-3 bg-primary text-primary-foreground rounded-xl font-inter font-semibold hover-lift contemplative-transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center min-w-[140px]"
              >
                {isLoading ? (
                  <>
                    <Icon name="ArrowPathIcon" size={16} className="mr-2 animate-spin" />
                    Subscribing...
                  </>
                ) : (
                  <>
                    <Icon name="PaperAirplaneIcon" size={16} className="mr-2" />
                    Subscribe
                  </>
                )}
              </button>
            </div>
          </form>

          <div className="mt-8 grid md:grid-cols-3 gap-6 text-center">
            <div className="flex flex-col items-center">
              <Icon name="ClockIcon" size={24} className="text-primary mb-2" />
              <h4 className="font-inter font-semibold text-foreground mb-1">
                Daily Delivery
              </h4>
              <p className="text-sm text-muted-foreground font-source-serif">
                Receive inspiration every morning at 6 AM
              </p>
            </div>
            
            <div className="flex flex-col items-center">
              <Icon name="HeartIcon" size={24} className="text-primary mb-2" />
              <h4 className="font-inter font-semibold text-foreground mb-1">
                Curated Content
              </h4>
              <p className="text-sm text-muted-foreground font-source-serif">
                Thoughtfully selected verses and reflections
              </p>
            </div>
            
            <div className="flex flex-col items-center">
              <Icon name="UserGroupIcon" size={24} className="text-primary mb-2" />
              <h4 className="font-inter font-semibold text-foreground mb-1">
                Community Focus
              </h4>
              <p className="text-sm text-muted-foreground font-source-serif">
                Join 50,000+ women in daily spiritual growth
              </p>
            </div>
          </div>

          <p className="text-xs text-muted-foreground text-center mt-6 font-inter">
            We respect your privacy. Unsubscribe at any time. No spam, ever.
          </p>
        </div>
      </div>
    </section>
  );
}